/**
 * Participant PII masking in the widget — the twin of the hosted consoles'
 * deutero_interviews/webui/static/shared/pii.js (docs/pii-masking.md); keep
 * the two in step (widget/test/pii.test.ts runs the same cases on both).
 *
 * When the study masks PII, typed answers are masked here before they leave
 * the participant's device and sent with `masked: true`; the placeholder
 * table stays in this browser (sessionStorage), so the interviewer's
 * "[GIVEN_NAME_1]" is shown as the real name. rampart and transformers.js are
 * loaded by dynamic import from jsDelivr only for studies that mask, so the
 * widget bundle itself stays small. A host page with a Content-Security-Policy
 * must allow cdn.jsdelivr.net (scripts, wasm) and huggingface.co plus its
 * CDNs (model files) — see docs/embed.md.
 *
 * Voice answers are transcribed on the server, which masks them there.
 */

export const RAMPART_URL = "https://cdn.jsdelivr.net/npm/@nationaldesignstudio/rampart@0.1.3/dist/index.js";
export const TRANSFORMERS_URL = "https://cdn.jsdelivr.net/npm/@huggingface/transformers@3.5.1";
export const PII_MODEL = "nationaldesignstudio/rampart";
export const PII_MODEL_REVISION = "b1993e4e68b082835b80ffc65acc03325ea2e501";
const MIN_SCORE = 0.4;
const LOAD_TIMEOUT_MS = 20000;

export const PLACEHOLDER = /\[[A-Z][A-Z_]*_\d+\]/g;
const ANY_PLACEHOLDER = /\[[A-Z][A-Z_]*_S?\d+\]/g;
const PARTIAL_TAIL = /\[[A-Z_]*(?:S?\d*)?$/;

export interface PiiSpan {
  start: number;
  end: number;
  label: string;
  text: string;
  score?: number;
  source?: string;
}

/** What masking needs to know about the question being answered. */
export interface PiiView {
  qtype?: string;
  options?: string[];
  groups?: string[];
  slots?: string[];
  scale?: { min_label?: string; max_label?: string } | null;
}

export interface PiiEngine {
  r: any;            // the rampart module
  classifier: any;   // rampart TokenClassifier, or null (recognizers only)
}

export interface MaskResult {
  text: string;
  masked: boolean;
  spans: PiiSpan[];
}

/* ------------------------------------------------------------ the engine */

let enginePromise: Promise<PiiEngine> | null = null;

// A runtime URL: bundlers must leave this import alone.
const importUrl = (url: string): Promise<any> => import(/* @vite-ignore */ /* webpackIgnore: true */ url);

export function adaptClassifier(pipe: any): any {
  const adapter: any = (text: string, opts?: unknown) => pipe(text, opts);
  const tok = pipe.tokenizer;
  if (tok?.encode) adapter.countTokens = (t: string) => tok.encode(t, { add_special_tokens: false }).length;
  if (tok?.tokenize) adapter.tokenize = (t: string) => tok.tokenize(t);
  return adapter;
}

async function loadEngine(): Promise<PiiEngine> {
  const r = await importUrl(RAMPART_URL);
  let classifier: any = null;
  try {
    const { pipeline } = await importUrl(TRANSFORMERS_URL);
    const pipe = await pipeline("token-classification", PII_MODEL, {
      dtype: "q4", device: "wasm", revision: PII_MODEL_REVISION,
    });
    classifier = adaptClassifier(pipe);
  } catch (e) {
    console.warn("[deutero] pii: classifier unavailable; the server will mask names", e);
  }
  return { r, classifier };
}

export function loadPiiEngine(): Promise<PiiEngine> {
  if (!enginePromise) {
    enginePromise = loadEngine().catch((e) => {
      console.warn("[deutero] pii: rampart unavailable; the server will mask", e);
      enginePromise = null;
      throw e;
    });
  }
  return enginePromise;
}

export function setPiiEngine(engine: PiiEngine | PromiseLike<PiiEngine> | null): void {
  enginePromise = engine ? Promise.resolve(engine) : null;
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error("pii engine load timed out")), ms)),
  ]);
}

export async function detectSpans(engine: PiiEngine, text: string): Promise<{ spans: PiiSpan[]; model: boolean }> {
  const { r, classifier } = engine;
  const heuristic = r.detectHeuristics(text);
  let all = heuristic;
  let model = false;
  if (classifier) {
    try {
      const map = r.premask(text, heuristic);
      const found = await r.detectNer(map.masked, classifier, MIN_SCORE);
      const projected = found.map((s: PiiSpan) => r.projectMaskedSpan(s, text, map)).filter(Boolean);
      all = [...heuristic, ...projected];
      model = true;
    } catch (e) {
      console.warn("[deutero] pii: classifier failed on this answer", e);
    }
  }
  const spans: PiiSpan[] = r.applyPolicy(all).sort((a: PiiSpan, b: PiiSpan) => a.start - b.start);
  return { spans, model };
}

/* ------------------------------------------------- what is not masked */

export function exemptFor(view: PiiView | null | undefined, text: string): (span: PiiSpan) => boolean {
  if (!view) return () => false;
  const scale = view.scale || {};
  const vocab = [...(view.options || []), ...(view.groups || []), ...(view.slots || []),
    scale.min_label || "", scale.max_label || ""]
    .filter((w) => w && w.trim()).map((w) => w.toLowerCase());
  const upload = view.qtype === "image_upload"
    ? ((text.match(/(?:https?:\/\/|\/media\/)\S+/i) || [])[0] || "").replace(/[.,;)]+$/, "")
    : "";
  return (span) => {
    const folded = span.text.toLowerCase();
    if (vocab.some((w) => w.includes(folded))) return true;
    return !!upload && span.label === "URL" && span.text.includes(upload);
  };
}

/* ------------------------------------------------- the placeholder table */

interface SavedTable {
  forward: [string, string][];
  counters: [string, number][];
  values: Record<string, string>;
}

export class EntityTable {
  private forward: Map<string, string>;
  private counters: Map<string, number>;
  private reverse = new Map<string, string>();

  constructor(saved?: SavedTable | null) {
    this.forward = new Map(saved?.forward || []);
    this.counters = new Map(saved?.counters || []);
    for (const token of this.forward.values()) {
      const value = saved?.values?.[token];
      if (typeof value === "string") this.reverse.set(token, value);
    }
  }

  placeholderFor(label: string, value: string): string {
    const key = `${label}:${value.toLowerCase().replace(/\s+/g, " ").trim()}`;
    const known = this.forward.get(key);
    if (known) return known;
    const next = (this.counters.get(label) || 0) + 1;
    this.counters.set(label, next);
    const token = `[${label}_${next}]`;
    this.forward.set(key, token);
    this.reverse.set(token, value);
    return token;
  }

  scrub(text: string, spans: PiiSpan[]): string {
    let out = text;
    for (const span of [...spans].sort((a, b) => b.start - a.start)) {
      out = `${out.slice(0, span.start)}${this.placeholderFor(span.label, span.text)}${out.slice(span.end)}`;
    }
    return out;
  }

  reveal(text: string): string {
    return text.replace(PLACEHOLDER, (token) => this.reverse.get(token) ?? token);
  }

  toJSON(): SavedTable {
    return {
      forward: [...this.forward], counters: [...this.counters],
      values: Object.fromEntries(this.reverse),
    };
  }
}

/* ----------------------------------------------------------- the masker */

export interface Masker {
  enabled: boolean;
  bind(interviewId: string): void;
  save(): void;
  mask(text: string, view?: PiiView | null): Promise<MaskResult>;
  reveal(text: string, opts?: { streaming?: boolean }): string;
}

const storageKey = (id: string) => `deutero.pii.${id}`;

function readSaved(id: string): SavedTable | null {
  try {
    const raw = sessionStorage.getItem(storageKey(id));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function createMasker(): Masker {
  let table = new EntityTable();
  let interviewId = "";
  const masker: Masker = {
    enabled: false,

    bind(id) {
      if (!id || id === interviewId) return;
      interviewId = id;
      const saved = readSaved(id);
      if (saved) {
        try { table = new EntityTable(saved); } catch { /* corrupt: start over */ }
      }
    },

    save() {
      if (!interviewId) return;
      try { sessionStorage.setItem(storageKey(interviewId), JSON.stringify(table)); } catch { /* private mode */ }
    },

    async mask(text, view = null) {
      // The contact step collects exactly these details, stored apart from
      // the transcript; the server leaves its replies alone too.
      if (!masker.enabled || !text || view?.qtype === "contact") return { text, masked: false, spans: [] };
      let engine: PiiEngine;
      try {
        engine = await withTimeout(loadPiiEngine(), LOAD_TIMEOUT_MS);
      } catch {
        return { text, masked: false, spans: [] };
      }
      const exempt = exemptFor(view, text);
      const { spans, model } = await detectSpans(engine, text);
      const kept = spans.filter((s) => !exempt(s));
      const out = table.scrub(text, kept);
      masker.save();
      return { text: out, masked: model, spans: kept };
    },

    reveal(text, { streaming = false } = {}) {
      if (!text) return text;
      let out = table.reveal(text);
      if (streaming) out = out.replace(PARTIAL_TAIL, "");
      return out;
    },
  };
  return masker;
}

export function stripPlaceholders(text: string): string {
  return (text || "")
    .replace(ANY_PLACEHOLDER, "")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/ +([,.!?;:])/g, "$1")
    .replace(/,(\s*[,.!?])/g, "$1")
    .trim();
}
